import { cn } from '@/lib/utils';
import { SliderInput } from '../shared/SliderInput';
import { Tip } from '../shared/constants';
import { UploadProfilePreview } from '../UploadProfilePreview';
import type { HubV2State, ProfileMode } from '../hooks/useHubV2State';

export default function StepOpwek({ state }: { state: HubV2State }) {
  const {
    pv, setPv, solarMode, setSolarMode,
    uploadedProfiles, uploadErrors, uploadLoading, handleSolarUpload, clearSolar,
  } = state;

  return (
    <div className="space-y-4">
      <div className="p-5 bg-white rounded-xl border border-[#D9E2EC] shadow-sm">
        <div className="text-sm font-bold text-[#0B1F4A] mb-1">☀️ PV Configuratie</div>
        <div className="text-xs text-[#8899AA] mb-4">Configureer de zonne-opwek capaciteit en het zonprofiel.</div>

        {/* Solar mode selector */}
        <div className="flex items-center gap-1.5 mb-3 p-2 rounded-lg bg-[#FFF7ED] border border-[#FED7AA]">
          {([
            ['standaard', '📊 Standaard', 'PV capaciteit handmatig instellen'],
            ['upload', '📂 Upload', 'Zonprofiel (12x24 matrix) importeren'],
          ] as [ProfileMode, string, string][]).map(([mode, label, tip]) => (
            <Tip key={mode} text={tip}>
              <button onClick={() => setSolarMode(mode)}
                className={cn(
                  "px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                  solarMode === mode
                    ? "bg-[#0B1F4A] text-white border-[#0B1F4A]"
                    : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]"
                )}>
                {label}
              </button>
            </Tip>
          ))}
          <span className="ml-auto text-xs font-bold text-[#0B1F4A] bg-white px-2 py-0.5 rounded-md border border-[#D9E2EC] tabular-nums">
            {pv} MWp
          </span>
        </div>

        {solarMode === 'standaard' && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
            <SliderInput label="PV Capaciteit" value={pv} set={setPv} min={0} max={50} step={0.5} unit="MWp" group="opwek" tooltip="Totaal geïnstalleerd PV-vermogen" />
          </div>
        )}

        {solarMode === 'upload' && (
          <div className="space-y-3">
            <div className="text-[10px] text-muted-foreground bg-muted/50 rounded-md border border-border px-3 py-2 leading-relaxed">
              📂 Upload een zonprofiel als 12×24 matrix (maand × uur) in CSV/Excel formaat. Waarden in kWh/kWp.
            </div>
            {!uploadedProfiles.solarProfile ? (
              <div>
                <input type="file" accept=".csv,.txt,.xlsx,.xls" id="hub-v2-solar-upload" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleSolarUpload(f); e.target.value = ''; }} />
                <button onClick={() => document.getElementById('hub-v2-solar-upload')?.click()}
                  disabled={uploadLoading.solar}
                  className="px-4 py-2 text-[11px] font-semibold rounded-lg border border-[#D9E2EC] bg-white text-[#0B1F4A] hover:bg-[#F5F8FB] cursor-pointer transition-colors disabled:opacity-50">
                  {uploadLoading.solar ? '⏳ Verwerken...' : '📂 Kies bestand'}
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-foreground">✅ Zonprofiel geladen</span>
                  <button onClick={clearSolar} className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">✕ Verwijderen</button>
                </div>
                {uploadedProfiles.solarStats && (
                  <div className="text-[10px] text-muted-foreground">
                    📊 {uploadedProfiles.solarStats.rows} datapunten | 📅 {uploadedProfiles.solarStats.dateStart} t/m {uploadedProfiles.solarStats.dateEnd}
                  </div>
                )}
              </div>
            )}
            {uploadErrors.solar && (
              <div className="text-[10px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">❌ {uploadErrors.solar}</div>
            )}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
              <SliderInput label="PV Capaciteit" value={pv} set={setPv} min={0} max={50} step={0.5} unit="MWp" group="opwek" tooltip="Totaal geïnstalleerd PV-vermogen" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
